import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ChatPanel from './ChatPanel'
import CodeEditor from './CodeEditor'
import ToolpathViewer from './ToolpathViewer'
import WorkspaceToolbar from './WorkspaceToolbar'
import ProjectDrawer from './ProjectDrawer'
import PipelineProgress from './PipelineProgress'
import { streamAgent } from '../../services/api'
import { getFingerprint } from '../../lib/fingerprint'
import type { GCodeResponse, JobConfig, WalkthroughStep } from '../../services/types'
import { parseToolpath, formatCycleTime } from '../../lib/gcode/toolpath-parser'
import type { Job, Message, MachineProfile, Tool, Operation, PipelineStep } from '../../types'
import { executeCodePatch } from '../../utils/executeCodePatch'
import WalkthroughOverlay from './WalkthroughOverlay'
import { AlertTriangle, X } from 'lucide-react'
import { WorkspaceEventProvider, useWorkspaceEvents } from '../../contexts/WorkspaceEventContext'

const SERVICE_DOWN_MSG = 'We\'re experiencing technical difficulties. Please try again later or contact support at help@gcodr.dev'

/** Compute cycle time from G-code using toolpath parser, falling back to LLM estimate */
function computeEstimatedTime(code: string, llmEstimate?: string): string {
  try {
    const data = parseToolpath(code)
    if (data.stats.estimatedCycleTimeSec > 0) {
      return formatCycleTime(data.stats.estimatedCycleTimeSec)
    }
  } catch { /* fall through */ }
  return llmEstimate ?? '—'
}

interface WorkspaceProps {
  job: Job
  updateJob: (updates: Partial<Job>) => void
  updateStreamingGCode: (opId: string, gcode: GCodeResponse) => void
  updateOperationGCode: (opId: string, gcode: GCodeResponse) => void
  addOperation: (op: Operation) => void
  removeOperation: (opId: string) => void
  profiles: MachineProfile[]
}

export default function Workspace(props: WorkspaceProps) {
  return (
    <WorkspaceEventProvider>
      <WorkspaceInner {...props} />
    </WorkspaceEventProvider>
  )
}

function WorkspaceInner({ job, updateJob, updateStreamingGCode, updateOperationGCode, addOperation, removeOperation, profiles }: WorkspaceProps) {
  const { emit, drain } = useWorkspaceEvents()
  const [isTyping, setIsTyping] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [walkthroughSteps, setWalkthroughSteps] = useState<WalkthroughStep[]>()
  const [walkthroughIndex, setWalkthroughIndex] = useState(0)

  // Toggle 3D viewer — close chat first so the flex layout is stable
  // before the Three.js Canvas mounts and sizes itself.
  const handleToggleViewer = useCallback(() => {
    if (viewerOpen) {
      // Closing viewer → immediate, restore chat
      setViewerOpen(false)
      setChatOpen(true)
    } else {
      // Opening viewer → close chat first, wait for exit animation (250ms),
      // then mount viewer into a stable layout
      setChatOpen(false)
      setTimeout(() => setViewerOpen(true), 300)
    }
  }, [viewerOpen])

  // Cmd+B / Ctrl+B to toggle chat panel, Cmd+3 / Ctrl+3 to toggle viewer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault()
        setChatOpen(prev => !prev)
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '3') {
        e.preventDefault()
        handleToggleViewer()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleToggleViewer])

  // Track how many messages existed when the pipeline finished,
  // so the chat panel only shows post-pipeline messages.
  const [chatStartIndex, setChatStartIndex] = useState(0)
  const wasPipelineRunning = useRef(false)

  // Ensure the workspace is fully visible — reset any residual scroll
  // from MissionControl and re-align on view transitions.
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollToWorkspace = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // On mount
  useEffect(() => { scrollToWorkspace() }, [])

  // On view transitions
  useEffect(() => { scrollToWorkspace() }, [viewerOpen, chatOpen])

  // Resolve live machine profile from machineId
  const machine = profiles.find(p => p.id === job.machineId)

  const activeOp = job.operations.find(op => op.id === job.activeOperationId) ?? job.operations[0]

  // Track previous tools for diff detection (seeded from job.tools)
  const prevTools = useRef<Tool[]>(job.tools)

  const buildJobConfig = useCallback((): JobConfig | null => {
    if (!machine || !activeOp) return null
    return {
      machine,
      operationType: activeOp.operationType,
      material: job.material,
      stockShape: job.stockShape,
      stockSize: job.stockSize,
      workOffsets: activeOp.workOffsets,
      tools: job.tools,
      notes: job.notes,
      units: job.units,
      drawingFilename: job.drawing?.name ?? null,
      workholding: machine.workholding ?? 'vise',
    }
  }, [job, machine, activeOp])

  const handleSendMessage = useCallback(async (text: string, imageFiles?: File[]) => {
    if (!text.trim() && (!imageFiles || imageFiles.length === 0)) return

    // Convert image files to base64 for storage + API
    let images: Array<{ base64: string; mimeType: string; name: string }> | undefined
    if (imageFiles && imageFiles.length > 0) {
      const { readFileAsBase64 } = await import('../../services/api')
      images = await Promise.all(
        imageFiles.map(async f => ({
          base64: await readFileAsBase64(f),
          mimeType: f.type || 'image/png',
          name: f.name,
        })),
      )
    }

    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      ...(images && { images }),
    }

    const withUser = [...job.messages, userMsg]

    // Create placeholder AI message for streaming
    const aiMsgId = crypto.randomUUID()
    const aiMsg: Message = { id: aiMsgId, role: 'assistant', content: '' }
    updateJob({ messages: [...withUser, aiMsg], pipelineSteps: [] })
    setIsTyping(true)

    // Mutable pipeline steps array — we mutate and snapshot to avoid stale closures
    let liveSteps: PipelineStep[] = []
    const emitSteps = () => updateJob({ pipelineSteps: [...liveSteps] })

    const upsertStep = (id: string, label: string, status: PipelineStep['status'], detail?: string) => {
      const existing = liveSteps.find(s => s.id === id)
      if (existing) {
        existing.status = status
        if (detail !== undefined) existing.detail = detail
      } else {
        liveSteps.push({ id, label, status, detail })
      }
      emitSteps()
    }

    try {
      const currentCode = activeOp?.gcode?.code ?? ''
      let accumulatedText = ''
      let liveCode = currentCode

      const jobConfig = buildJobConfig()
      if (!jobConfig) throw new Error('Machine profile not found')

      // Drain accumulated workspace events (code edits, tool changes, etc.)
      const contextDeltas = drain()

      const operationSections = job.operations
        .filter(op => op.gcode?.code)
        .map(op => ({ id: op.id, label: op.label, code: op.gcode!.code }))

      await streamAgent(
        {
          jobId: job.id,
          message: text.trim(),
          jobConfig,
          currentCode,
          sessionId: job.agentSessionId,
          activeOperationId: job.activeOperationId,
          memory: job.memory,
          fingerprint: getFingerprint(),
          extractedGeometry: job.extractedGeometry,
          pdfBase64: job.pdfBase64 ?? null,
          operationSections: operationSections.length > 0 ? operationSections : undefined,
          images: images?.map(({ base64, mimeType }) => ({ base64, mimeType })),
          contextDeltas: contextDeltas.length > 0 ? contextDeltas : undefined,
        },
        {
          onSession(sessionId) {
            updateJob({ agentSessionId: sessionId })
          },
          onToken(token) {
            accumulatedText += token
            updateJob({
              messages: [...withUser, { ...aiMsg, content: accumulatedText }],
            })
          },
          onWriteGCode(code, estimatedTime, operationId, label) {
            if (operationId) {
              // Per-operation section — create or update the operation tab
              const existing = job.operations.find(op => op.id === operationId)
              if (!existing) {
                const newOp: Operation = {
                  id: operationId,
                  label: label ?? operationId,
                  operationType: activeOp?.operationType ?? 'milling',
                  workOffsets: activeOp?.workOffsets ?? [{ register: 'G54' as const, x: 0, y: 0, z: 0 }],
                  gcode: { code, lines: code.split('\n').length, estimatedTime: computeEstimatedTime(code, estimatedTime) },
                  isGenerating: false,
                }
                addOperation(newOp)
              } else {
                updateOperationGCode(operationId, {
                  code,
                  lines: code.split('\n').length,
                  estimatedTime: computeEstimatedTime(code, estimatedTime),
                })
              }
            } else {
              // Combined assembled program — update the original "Full Program" tab
              if (activeOp) {
                const gcode: GCodeResponse = {
                  code,
                  lines: code.split('\n').length,
                  estimatedTime: computeEstimatedTime(code, estimatedTime),
                }
                updateOperationGCode(activeOp.id, gcode)
                updateJob({ activeOperationId: activeOp.id })
                liveCode = code
              }
              // Mark assembling step as done
              upsertStep('assemble', 'Assembling program', 'done')
            }
          },
          onPatchGCode(patch, operationId) {
            if (operationId) {
              // Per-operation patch — find the target operation and apply
              const targetOp = job.operations.find(op => op.id === operationId)
              if (targetOp?.gcode) {
                const patched = executeCodePatch(targetOp.gcode.code, patch)
                updateOperationGCode(operationId, {
                  ...targetOp.gcode,
                  code: patched,
                  lines: patched.split('\n').length,
                })
              }
            } else if (activeOp?.gcode) {
              liveCode = executeCodePatch(liveCode, patch)
              updateStreamingGCode(activeOp.id, {
                ...activeOp.gcode,
                code: liveCode,
                lines: liveCode.split('\n').length,
              })
            }
          },
          onGeometry(geometry) {
            updateJob({ extractedGeometry: geometry })
          },
          onValidation(issues) {
            const hasErrors = issues.some(i => i.severity === 'error')
            upsertStep('validate_config', 'Validating configuration',
              hasErrors ? 'error' : 'done',
              issues.length > 0 ? `${issues.length} issue${issues.length !== 1 ? 's' : ''}` : undefined
            )
          },
          onMemory(content) {
            updateJob({ memory: content })
          },
          onOperatorNotes(notes) {
            updateJob({ extractedSummary: notes })
          },
          onToolCall(tool, summary) {
            // Map tool_call events to pipeline steps
            const stepMap: Record<string, string> = {
              'validate_config': 'Validating configuration',
              'extract_geometry': 'Extracting geometry',
              'plan_operations': 'Planning operations',
              'enforce_geometry': 'Enforcing geometry constraints',
              'write_section': summary.replace('Writing toolpath: ', ''),
            }
            const label = stepMap[tool] || summary
            if (tool === 'validate_config' || tool === 'extract_geometry' || tool === 'plan_operations' || tool === 'enforce_geometry') {
              upsertStep(tool, label, 'running')
            }
          },
          onPipelinePlan(operations) {
            // Mark earlier steps as done
            upsertStep('extract_geometry', 'Extracting geometry', 'done')
            upsertStep('plan_operations', 'Planning operations', 'done', `${operations.length} ops`)
            upsertStep('enforce_geometry', 'Enforcing geometry constraints', 'done')

            // Rename the original operation to "Full Program" since individual ops will be added
            if (activeOp && operations.length > 1) {
              updateJob({ isPlanningOperations: true })
              const updatedOps = job.operations.map(op =>
                op.id === activeOp.id ? { ...op, label: 'Full Program' } : op,
              )
              updateJob({ operations: updatedOps })
            }

            // Create "Generating toolpaths" parent with operation sub-steps
            const children: PipelineStep[] = operations.map(op => ({
              id: op.id,
              label: op.label,
              status: 'pending' as const,
            }))
            const toolpathStep: PipelineStep = {
              id: 'toolpaths',
              label: 'Generating toolpaths',
              status: 'running',
              detail: `0/${operations.length}`,
              children,
            }
            liveSteps.push(toolpathStep)
            liveSteps.push({ id: 'assemble', label: 'Assembling program', status: 'pending' })
            emitSteps()
          },
          onPipelineStep(operationId, _label, status) {
            // Update the specific operation sub-step under "Generating toolpaths"
            const toolpathStep = liveSteps.find(s => s.id === 'toolpaths')
            if (toolpathStep?.children) {
              const child = toolpathStep.children.find(c => c.id === operationId)
              if (child) child.status = status

              const doneCount = toolpathStep.children.filter(c => c.status === 'done').length
              const total = toolpathStep.children.length
              toolpathStep.detail = `${doneCount}/${total}`

              if (doneCount === total) {
                toolpathStep.status = 'done'
                // Start assembling
                upsertStep('assemble', 'Assembling program', 'running')
              }

              if (status === 'error') {
                toolpathStep.status = 'error'
              }
            }
            emitSteps()
          },
          onWalkthrough(steps) {
            setWalkthroughSteps(steps)
            setWalkthroughIndex(0)
          },
          onPdfReview(data) {
            if (!data.match && data.discrepancies.length > 0) {
              updateJob({ pdfDiscrepancies: data.discrepancies })
              upsertStep('pdf_review', 'PDF validation', 'error', `${data.discrepancies.length} discrepancies`)
            } else {
              upsertStep('pdf_review', 'PDF validation', 'done', 'Geometry matches PDF')
            }
          },
          onDone(sessionId) {
            // Drop the placeholder AI message if no text ever arrived (safety net)
            const finalMessages = accumulatedText
              ? [...withUser, { ...aiMsg, content: accumulatedText }]
              : withUser
            updateJob({
              messages: finalMessages,
              agentSessionId: sessionId,
              isPlanningOperations: false,
              planningText: undefined,
            })
          },
          onError(message) {
            accumulatedText += `\n\n**Error:** ${message}`
            // Mark all non-done pipeline steps as error so the UI shows failure
            for (const step of liveSteps) {
              if (step.status !== 'done') step.status = 'error'
              if (step.children) {
                for (const child of step.children) {
                  if (child.status !== 'done') child.status = 'error'
                }
              }
            }
            emitSteps()
            updateJob({
              messages: [...withUser, { ...aiMsg, content: accumulatedText }],
              isPlanningOperations: false,
              planningText: undefined,
            })
          },
        },
      )
    } catch (err) {
      console.error('Agent error:', err)
      const errorMsg: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: SERVICE_DOWN_MSG,
      }
      updateJob({ messages: [...withUser, errorMsg] })
    } finally {
      setIsTyping(false)
    }
  }, [job, activeOp, updateJob, updateStreamingGCode, updateOperationGCode, buildJobConfig])

  // Auto-generate G-code for a brand new job (no user messages yet, no G-code)
  const autoGenTriggered = useRef(false)
  useEffect(() => {
    if (autoGenTriggered.current) return
    const hasGCode = job.operations.some(op => op.gcode !== null)
    // If the job already has G-code, never regenerate (even if messages failed to persist)
    if (hasGCode) {
      autoGenTriggered.current = true
      return
    }
    const hasUserMessages = job.messages.some(m => m.role === 'user')
    if (!hasUserMessages) {
      autoGenTriggered.current = true
      handleSendMessage('Generate the G-code program.')
    }
  }, [job.messages, job.operations, handleSendMessage])

  const handleSettingsChange = useCallback(
    (updates: Partial<Pick<Job, 'material' | 'units'>>, regenerate: boolean) => {
      const oldMaterial = job.material
      updateJob(updates)
      if (regenerate && updates.material && updates.material !== oldMaterial) {
        handleSendMessage(
          `Material changed from "${oldMaterial}" to "${updates.material}". ` +
          `Recalculate all feeds, speeds, and depth of cut for the new material. ` +
          `Check RPM, feed rate, and coolant recommendations, then regenerate the G-code.`
        )
      } else if (regenerate) {
        handleSendMessage(`Regenerate the G-code program for the current operation with the updated settings.`)
      }
    },
    [updateJob, handleSendMessage, job.material]
  )

  const handleToolsChange = useCallback((tools: Tool[]) => {
    // Persist tools on the job
    updateJob({ tools })

    // Detect added or edited tools
    const prev = prevTools.current
    const added = tools.filter(t => !prev.find(pt => pt.id === t.id))
    const edited = tools.filter(t => {
      const p = prev.find(pt => pt.id === t.id)
      return p && JSON.stringify(p) !== JSON.stringify(t)
    })

    if (added.length > 0 || edited.length > 0) {
      const summary = tools.map(t =>
        `T${String(t.number).padStart(2, '0')} ${t.diameter}mm ${t.type} (${t.flutes}fl, stickout: ${t.stickout}mm, ${t.canPlunge ? 'can plunge' : 'ramp only'})`
      ).join('; ')

      const note: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `[Tool crib updated] Current tools: ${summary}`,
      }
      updateJob({ messages: [...job.messages, note] })
    }

    prevTools.current = tools
  }, [job.messages, updateJob])

  const handleCodeChange = useCallback((gcode: GCodeResponse) => {
    if (!activeOp) return
    updateOperationGCode(activeOp.id, gcode)
  }, [activeOp, updateOperationGCode])

  const handleWorkOffsetChange = useCallback((offset: { register: 'G54' | 'G55' | 'G56' | 'G57' | 'G58' | 'G59'; x: number; y: number; z: number }) => {
    if (!activeOp) return
    emit({ type: 'work_offset_change', register: offset.register, x: offset.x, y: offset.y, z: offset.z })
    const updatedOps = job.operations.map(op =>
      op.id === activeOp.id
        ? { ...op, workOffsets: [offset] }
        : op,
    )
    updateJob({ operations: updatedOps })
  }, [activeOp, job.operations, updateJob, emit])

  const handleAddOperation = useCallback(() => {
    if (!machine) return
    const opId = crypto.randomUUID()
    const opNumber = (job.operations.length + 1) * 10
    const newOp: Operation = {
      id: opId,
      label: `Op ${opNumber}`,
      operationType: 'milling',
      workOffsets: [{ register: 'G54' as const, x: 0, y: 0, z: 0 }],
      gcode: null,
      isGenerating: false,
    }
    addOperation(newOp)
    // Agent will generate G-code when the user sends their first message for this operation
  }, [machine, addOperation])

  // Guard: if machine profile not found, show error state
  if (!machine) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: '100vh' }}
      >
        <p className="font-mono text-sm text-slate-500">Machine profile not found.</p>
      </div>
    )
  }

  const currentStep = walkthroughSteps?.[walkthroughIndex]
  const highlightRange = currentStep
    ? { startLine: currentStep.startLine, endLine: currentStep.endLine }
    : undefined

  // Show pipeline progress board during initial generation (no code yet)
  const hasCode = Boolean(activeOp?.gcode?.code)
  const isPipelineRunning = isTyping && !hasCode

  // When pipeline finishes, snapshot the message count so chat starts clean
  useEffect(() => {
    if (wasPipelineRunning.current && !isPipelineRunning) {
      setChatStartIndex(job.messages.length)
    }
    wasPipelineRunning.current = isPipelineRunning
  }, [isPipelineRunning, job.messages.length])

  if (!machine) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh' }}>
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="font-mono text-lg text-slate-100">No Machine Configured</h2>
          <p className="font-mono text-sm text-slate-500 leading-relaxed">
            This job requires a machine profile to generate G-code. Set up your machine first.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard/machines'}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg font-mono text-sm bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 transition-all"
          >
            Configure Machine
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col overflow-hidden"
      style={{ height: '100vh' }}
    >
      {/* Toolbar */}
      <WorkspaceToolbar
        gcode={activeOp?.gcode ?? null}
        isGenerating={activeOp?.isGenerating ?? false}
        isPlanningOperations={job.isPlanningOperations}
        machine={machine}
        tools={job.tools}
        material={job.material}
        operationType={activeOp?.operationType ?? 'milling'}
        onSettingsChange={handleSettingsChange}
        onToolsChange={handleToolsChange}
        chatOpen={chatOpen}
        onToggleChat={() => setChatOpen(prev => !prev)}
        viewerOpen={viewerOpen}
        onToggleViewer={handleToggleViewer}
        drawerOpen={drawerOpen}
        onToggleDrawer={() => setDrawerOpen(prev => !prev)}
      />

      {/* PDF discrepancy warning banner */}
      {job.pdfDiscrepancies && job.pdfDiscrepancies.length > 0 && (
        <div className="mx-3 mt-1 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 font-mono text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="text-amber-300 font-semibold">PDF Validation: {job.pdfDiscrepancies.length} discrepanc{job.pdfDiscrepancies.length === 1 ? 'y' : 'ies'} found</span>
              <ul className="mt-1 space-y-0.5 text-amber-400/80">
                {job.pdfDiscrepancies.map((d, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="shrink-0">-</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => updateJob({ pdfDiscrepancies: [] })}
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Split pane: drawer + left panel + code editor */}
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0 flex">
          {/* Project drawer */}
          <AnimatePresence initial={false}>
            {drawerOpen && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 260, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="h-full shrink-0 overflow-hidden"
              >
                <ProjectDrawer
                  currentJobId={job.id}
                  onClose={() => setDrawerOpen(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex-1 min-w-0 flex px-3 pt-3">
          {/* Left: Pipeline progress during generation, Chat panel when toggled */}
          <AnimatePresence initial={false}>
            {(isPipelineRunning || chatOpen) && (
              <motion.div
                initial={{ opacity: 0, width: 0, marginRight: 0 }}
                animate={{ opacity: 1, width: 340, marginRight: 12 }}
                exit={{ opacity: 0, width: 0, marginRight: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="h-full shrink-0 overflow-hidden"
              >
                <AnimatePresence mode="wait">
                  {isPipelineRunning ? (
                    <motion.div
                      key="pipeline"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="h-full flex items-start justify-center pt-6 overflow-y-auto"
                    >
                      <PipelineProgress
                        steps={job.pipelineSteps}
                        jobLabel={`${job.material} · ${job.label}`}
                        operatorNotes={job.extractedSummary}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="chat"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25 }}
                      className="h-full"
                    >
                      <div data-tour="chat" className="h-full">
                        <ChatPanel
                          messages={job.messages.slice(chatStartIndex)}
                          isTyping={isTyping}
                          sendMessage={handleSendMessage}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Right: Code Editor or 3D Viewer */}
          <div className="flex-1 min-w-0 h-full relative">
            {viewerOpen ? (
              <div className="h-full glass rounded-t-xl overflow-hidden">
                <ToolpathViewer
                  gcode={activeOp?.gcode?.code ?? ''}
                  geometry={job.extractedGeometry}
                  tools={job.tools}
                  workOffsets={activeOp?.workOffsets}
                  onWorkOffsetChange={handleWorkOffsetChange}
                />
              </div>
            ) : (
              <div data-tour="code-editor">
                <CodeEditor
                  operations={job.operations}
                  activeOperation={activeOp}
                  onSelectOperation={(opId) => updateJob({ activeOperationId: opId })}
                  onAddOperation={handleAddOperation}
                  onRemoveOperation={removeOperation}
                  onCodeChange={handleCodeChange}
                  isPlanningOperations={job.isPlanningOperations}
                  planningText={job.planningText}
                  highlightRange={highlightRange}
                />
                {walkthroughSteps && (
                  <WalkthroughOverlay
                    steps={walkthroughSteps}
                    currentIndex={walkthroughIndex}
                    onPrev={() => setWalkthroughIndex(i => Math.max(0, i - 1))}
                    onNext={() => setWalkthroughIndex(i => Math.min(walkthroughSteps.length - 1, i + 1))}
                    onClose={() => setWalkthroughSteps(undefined)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
      {/* Tour disabled for now — trigger manually when ready */}
    </div>
  )
}
